// @ts-check
import React from 'react'
import { Link as GatsbyLink } from 'gatsby'

/**
 * Since DOM elements <a> cannot receive activeClassName,
 * destructure the prop here and pass it only to GatsbyLink
 *
 * @param {Object} arguments
 * @param {any} arguments.children - Component chidlren.
 * @param {(import('twin.macro').TwStyle | string)[]} arguments.css - Optional injected styles.
 * @param {string} arguments.to - URL to link to.
 * @param {string=} arguments.activeClassName - Additional classnames.
 * @param {boolean=} arguments.download - Is this a download?
 * @param {boolean} arguments.shouldBeTracked - Should this be tracked?
 * @param {string} arguments.description - Description
 * @param {('nl' | 'en')=} arguments.language - i18n key.
 */
const Link = ({ children, to, activeClassName, download = false, shouldBeTracked, description, language, ...other }) => {
    // Tailor the following test to your environment.
    // This example assumes that any internal link (intended for Gatsby)
    // will start with exactly one slash, and that anything else is external.
    const internal = /^\/(?!\/)/.test(to)

    /**
     * If it's an internal URL ('to'), and the requested language
     * is not Dutch (e.g. 'English' / 'en'), we can add the i18n
     * prefix to the URL, so that we are routed to a page in
     * the same language as requested. Make sure it exists.
     */
    const internalTo = internal ? `${language && language !== 'nl' ? `/${language}` : ''}${to}` : to

    if (shouldBeTracked) {
        return (
            <GatsbyLink to={internalTo} activeClassName={activeClassName} {...other}>
                {children}
            </GatsbyLink>
        )
    }

    // Use Gatsby Link for internal links, and <a> for others
    if (internal && !download) {
        return (
            <GatsbyLink to={internalTo} activeClassName={activeClassName} {...other}>
                {children}
            </GatsbyLink>
        )
    }

    // This implementation is for the extrenal links that should not be opened in the new tab so we made an array of those links
    // and check if the 'to' prop is in that array, if so we set target to '_self' otherwise '_blank'
    const sameWindowLinks = ['https://shine.co/nl/legal/terms', 'https://shine.co/nl/legal/cookies', 'https://shine.co/nl/legal/privacy']
    const isSameWindow = sameWindowLinks.includes(to)

    return (
        /* eslint-disable-next-line react/jsx-no-target-blank */
        <a target={isSameWindow ? '_self' : '_blank'} href={to} {...(typeof to === 'string' && to.indexOf('.tellow.nl') === -1 ? { rel: 'noopener noreferrer' } : {})} {...other}>
            {children}
        </a>
    )
}

export default Link
